"use client";

/**
 * Search + category filter over the manufacturer matrix AND the detailed
 * accordions (kept in sync from one filtered list). All entries render on the
 * server too, so they're in the HTML for SEO; the filter only shows/hides on the
 * client. Fires manufacturer_filter_use (cta_location = "search" | "category").
 */
import * as React from "react";
import posthog from "posthog-js";
import { ManufacturerMatrix } from "./manufacturer-matrix";
import { ManufacturerSection } from "./manufacturer-client";
import type { ManufacturerAccessEntry } from "@/lib/seo/content/types";

const CATEGORY_LABEL: Record<string, string> = {
  semiconductor: "Semiconductor",
  memory: "Memory",
  power: "Power",
  analog: "Analog",
  discrete: "Discrete",
  passive: "Passive",
  other: "Other",
};

function fireFilter(kind: string) {
  try {
    posthog.capture("manufacturer_filter_use", {
      source_page: "/resources/manufacturer-pcn-directory",
      cta_location: kind,
    });
  } catch {
    /* analytics must never break the page */
  }
}

export function ManufacturerExplorer({
  manufacturers,
  pcnsharkIntake,
  pcnsharkHref,
}: {
  manufacturers: ManufacturerAccessEntry[];
  pcnsharkIntake: string[];
  pcnsharkHref: string;
}) {
  const [query, setQuery] = React.useState("");
  const [category, setCategory] = React.useState("all");

  const categories = React.useMemo(
    () =>
      Array.from(
        new Set(manufacturers.map((m) => m.category).filter(Boolean) as string[]),
      ).sort(),
    [manufacturers],
  );

  const filtered = manufacturers.filter((m) => {
    const q = query.trim().toLowerCase();
    if (q && !m.name.toLowerCase().includes(q)) return false;
    if (category !== "all" && m.category !== category) return false;
    return true;
  });

  const active = query.trim() !== "" || category !== "all";

  const reset = () => {
    setQuery("");
    setCategory("all");
  };

  return (
    <div>
      {/* Filter bar */}
      <div className="border rule bg-paper-2 p-4 lg:p-5 flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onBlur={() => {
            if (query.trim()) fireFilter("search");
          }}
          placeholder="Search manufacturers…"
          aria-label="Search manufacturers"
          className="form-cell flex-1 min-w-[200px] text-[14px] py-2 px-3 border rule bg-white"
        />
        {categories.length > 1 && (
          <>
            <label className="sr-only" htmlFor="mfr-category">
              Filter by category
            </label>
            <select
              id="mfr-category"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                fireFilter("category");
              }}
              className="form-cell w-full sm:w-auto text-[13px] py-2 px-3 border rule bg-white"
            >
              <option value="all">All categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABEL[c] ?? c}
                </option>
              ))}
            </select>
          </>
        )}
        {active && (
          <button
            type="button"
            onClick={reset}
            className="font-mono text-[11px] uppercase tracking-wider ink-3 hover:text-[color:var(--ink)] ulink"
          >
            Clear
          </button>
        )}
      </div>

      <p
        className="mt-3 font-mono text-[11px] uppercase tracking-wider ink-3"
        aria-live="polite"
      >
        Showing {filtered.length} of {manufacturers.length} manufacturers
      </p>

      {filtered.length > 0 ? (
        <>
          <div className="mt-4">
            <ManufacturerMatrix manufacturers={filtered} />
          </div>

          <div className="mt-12">
            <h3 className="font-display font-semibold text-[20px] lg:text-[24px] tracking-[-0.02em] mb-2">
              Manufacturer-by-manufacturer access
            </h3>
            <p className="text-[14px] ink-2 leading-relaxed max-w-[70ch] mb-6">
              Each entry is individually verified against the manufacturer&rsquo;s
              own materials. Expand one for how its notices work, what your team
              should do, and how to process its PCNs in PCNshark.
            </p>
            <div className="space-y-3">
              {filtered.map((m) => (
                <ManufacturerSection
                  key={m.id}
                  entry={m}
                  pcnsharkIntake={pcnsharkIntake}
                  pcnsharkHref={pcnsharkHref}
                />
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="mt-4 border rule bg-white p-8 text-[14px] ink-2">
          No manufacturers match those filters.{" "}
          <button
            type="button"
            onClick={reset}
            className="ulink hover:text-[color:var(--ink)]"
          >
            Clear filters
          </button>
          .
        </div>
      )}
    </div>
  );
}
